import { Box, CircularProgress, debounce, Grid, IconButton, TextField } from "@material-ui/core";
import ClearIcon from "@material-ui/icons/Clear";
import SearchIcon from "@material-ui/icons/Search";
import { Autocomplete } from "@material-ui/lab";
import { useEffect, useState } from "react";
import { AddressDto, AddressLine, MetadataResult, useContextField } from "../../../api";
import { useSharedStyles } from "../../../common/sharedStyles";
import { useAppDispatch, useAppSelector } from "../../../store";
import { clear, getMetadataAsync, searchAsync } from "../../../store/addressFinder";
import { Colors, ErrorMessage, FormLabel } from "../helpers";

enum Mode {
  None,
  Searching,
  Selecting,
}

export default (props: { name: string; disabled?: boolean; autoFocus?: boolean; label?: string; onClear?: () => void }) => {
  const sharedClasses = useSharedStyles();
  const { addresses, addressInfo, isLoading } = useAppSelector((state) => state.addressFinder);
  const [field, _, helper] = useContextField<AddressDto | undefined>(props.name);
  const { onClear, label, ...autocompleteProps } = props;
  const [mode, setMode] = useState<Mode>(Mode.None);
  const dispatch = useAppDispatch();

  const addressSearch = debounce((value: string) => {
    if (mode === Mode.Selecting) return;

    if (value.length >= 3) {
      setMode(Mode.Searching);
      dispatch(searchAsync(value));
    } else {
      setMode(Mode.None);
      dispatch(clear());
    }
  }, 999);

  const selectAddress = (addressInfo: MetadataResult) => {
    const fullAddress: AddressDto = {
      streetAddress: "",
      suburb: "",
      city: "",
      postCode: "",
      country: "New Zealand",
    };

    const rawAddressLines = (addressInfo.postal || addressInfo.a || "").split(",");
    const addressLines = new Array();
    let index = 0;

    for (let i = 0; i < rawAddressLines.length; i++) {
      rawAddressLines[i] = rawAddressLines[i].replace(/^\s+|\s+$/g, "");
      if (rawAddressLines[i] !== addressInfo.buildingName) addressLines[index++] = rawAddressLines[i];
    }

    /* set City/Postcode*/
    const city = addressInfo.mailtown || addressInfo.city || "";
    if (addressLines[addressLines.length - 1] === city + " " + addressInfo.postcode) {
      fullAddress.city = city;
      fullAddress.postCode = addressInfo.postcode || "";
      addressLines.pop();
    }

    /* set Suburb */
    const suburb = addressInfo.postSuburb || addressInfo.suburb || "";
    if (addressLines[addressLines.length - 1] === suburb) {
      fullAddress.suburb = suburb;
      addressLines.pop();
    } else if (addressLines[addressLines.length - 1].indexOf("RD") === 0) {
      fullAddress.suburb = addressLines[addressLines.length - 1];
      addressLines.pop();
    }

    /* set street address */
    fullAddress.streetAddress = addressLines.join(", ");
    helper.setValue(fullAddress);
    setMode(Mode.None);
    dispatch(clear());
  };

  useEffect(() => {
    mode === Mode.Selecting && addressInfo?.success && selectAddress(addressInfo);
  }, [mode, addressInfo]);

  return (
    <>
      <FormLabel label={label} />
      {field.value ? (
        <Grid container spacing={3}>
          <Grid item md={6} xs={12}>
            <Box style={{ borderRadius: "0.75rem", border: `1px solid ${Colors.Grey}`, backgroundColor: Colors.White }}>
              <Grid container justifyContent="space-between">
                <Grid item>
                  <Box py={1} px={2}>
                    <Grid container direction="column">
                      <Grid item>{field.value.streetAddress}</Grid>
                      {!!field.value.suburb && <Grid item>{field.value.suburb}</Grid>}
                      <Grid item>
                        {field.value.city} {field.value.postCode}
                      </Grid>
                      <Grid item>{field.value.country}</Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid item>
                  <IconButton
                    style={{ color: Colors.Black }}
                    onClick={(_) => {
                      helper.setValue(undefined);
                      onClear?.();
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      ) : (
        <Autocomplete
          {...autocompleteProps}
          filterOptions={(options) => options}
          openOnFocus
          disableClearable
          forcePopupIcon={false}
          loading={isLoading}
          options={addresses || []}
          onInputChange={(event, value) => event.type === "change" && addressSearch(value)}
          getOptionLabel={(addressLine: AddressLine) => addressLine.a || ""}
          onChange={(_, value: AddressLine | null) => {
            if (value?.pxid) {
              setMode(Mode.Selecting);
              dispatch(getMetadataAsync(value.pxid));
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              className={sharedClasses.input}
              InputProps={{
                ...params.InputProps,
                style: { padding: "5px 8px 6px 8px" },
                endAdornment: isLoading ? <CircularProgress color="inherit" size="1.35rem" style={{ marginRight: 5 }} /> : <SearchIcon style={{ marginRight: 5 }} />,
              }}
            />
          )}
        />
      )}
      <ErrorMessage name={field?.name} />
    </>
  );
};
